import React, { useState, useCallback } from 'react';
import { FiUser, FiFileText, FiUsers, FiX, FiList } from 'react-icons/fi';
import '../../styles/InsuredMentionDropdown.css';

const IcoExternalLink: React.FC<{ size?: number }> = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
    <polyline points="15 3 21 3 21 9"/>
    <line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);


// ── API response shapes ────────────────────────────────────────────────────────
interface PolicyDetail {
  databaseId: string;
  number: string | null;
  status: string | null;
  active: boolean;
  carrierName: string | null;
  linesOfBusiness: string[];
  effectiveDate: string | null;
  expirationDate: string | null;
  insuredDatabaseId: string;
}

interface DriverDetail {
  databaseId: string;
  firstName: string | null;
  lastName: string | null;
  licenseNumber: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  maritalStatus: string | null;
  excludedDriver: boolean;
}

interface VehicleDetail {
  databaseId: string;
  year: number | null;
  make: string | null;
  model: string | null;
  vin: string | null;
  use: string | null;
  bodyType: string | null;
}

// ── Public InsuredResult type ──────────────────────────────────────────────────
export interface InsuredResult {
  id: string;
  name: string;
  personalType_FirstName: string | null;
  personalType_LastName: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  stateAbv: string | null;
  addressLine1: string | null;
  zipCode: string | null;
  type: number;          // 0=Insured, 1=Prospect, 700=Policy
  typeName: string;      // "Insured" | "Prospect" | "Policy"
  policyNumber: string | null;
}

interface InsuredMentionDropdownProps {
  query: string;
  results: InsuredResult[];
  loading: boolean;
  activeIdx: number;
  onSelect: (insured: InsuredResult) => void;
  onActiveChange: (idx: number) => void;
  onExpandedChange?: (expanded: boolean) => void;
}

// ── Detail panel state ─────────────────────────────────────────────────────────
type DetailTab = 'policies' | 'drivers' | 'vehicles';

interface DetailPanel {
  insured: InsuredResult;
  tab: DetailTab;
  policies: InsuredResult[];
  drivers: DriverDetail[];
  vehicles: VehicleDetail[];
  loadingPolicies: boolean;
  loadingDrivers: boolean;
  loadingVehicles: boolean;
}

// ── Helpers ────────────────────────────────────────────────────────────────────
function getInitials(name: string): string {
  return name.split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

type RecordType = 'insured' | 'prospect' | 'policy' | 'carrier';

function getRecordType(ins: InsuredResult): RecordType {
  const t = (ins.typeName || '').toLowerCase();
  if (t === 'policy')   return 'policy';
  if (t === 'prospect') return 'prospect';
  if (t === 'carrier')  return 'carrier';
  return 'insured';
}

const ShieldIcon: React.FC<{ size?: number }> = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

function getTypeIcon(rt: RecordType) {
  if (rt === 'policy')  return <FiFileText size={12} />;
  if (rt === 'prospect') return <FiUsers size={12} />;
  if (rt === 'carrier') return <ShieldIcon size={12} />;
  return <FiUser size={12} />;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
}

function mapPolicyDetails(raw: PolicyDetail[]): InsuredResult[] {
  return raw.map(p => ({
    id: p.databaseId,
    name: [p.carrierName, p.status].filter(Boolean).join(' · ') || '',
    policyNumber: p.number,
    typeName: 'Policy', type: 700,
    personalType_FirstName: null, personalType_LastName: null,
    email: null, phone: null, city: null, stateAbv: null, addressLine1: null, zipCode: null,
  }));
}

// ── Main component ─────────────────────────────────────────────────────────────
const InsuredMentionDropdown: React.FC<InsuredMentionDropdownProps> = ({
  query, results, loading, activeIdx, onSelect, onActiveChange, onExpandedChange,
}) => {
  const [detail, setDetail] = useState<DetailPanel | null>(null);

  React.useEffect(() => {
    onExpandedChange?.(detail !== null);
  }, [detail, onExpandedChange]);

  const fetchPolicies = useCallback(async (insured: InsuredResult): Promise<InsuredResult[]> => {
    const authToken = localStorage.getItem('authToken') || '';
    const amsToken  = localStorage.getItem('amsToken')  || '';
    const res = await fetch(
      `${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/ampnowcerts/insuredpoliciesbyid?insuredId=${encodeURIComponent(insured.id)}`,
      { headers: { Authorization: `Bearer ${authToken}`, NCToken: amsToken } }
    );
    if (!res.ok) throw new Error('Policy fetch failed');
    const data = await res.json();
    const raw: PolicyDetail[] = Array.isArray(data)
      ? data
      : (data?.policies ?? (data?.databaseId ? [data] : []));
    return mapPolicyDetails(raw);
  }, []);

  const fetchCarrierPolicies = useCallback(async (insured: InsuredResult): Promise<InsuredResult[]> => {
    const amsToken = localStorage.getItem('amsToken') || '';
    const filterValue = `(carrierName eq '${insured.name.replace(/'/g, "''")}')`;
    const res = await fetch(
      `${(process.env.REACT_APP_Nowcerts_Base_Url || '')}/api/odata/PolicyDetailList?$filter=${encodeURIComponent(filterValue)}&$top=1000`,
      { headers: { Authorization: `Bearer ${amsToken}` } }
    );
    if (!res.ok) throw new Error('Carrier policy fetch failed');
    const data = await res.json();
    const raw: PolicyDetail[] = Array.isArray(data) ? data : (data?.value ?? []);
    return mapPolicyDetails(raw);
  }, []);

  const fetchDrivers = useCallback(async (insured: InsuredResult): Promise<DriverDetail[]> => {
    const authToken = localStorage.getItem('authToken') || '';
    const amsToken  = localStorage.getItem('amsToken')  || '';
    const res = await fetch(
      `${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/ampnowcerts/insured/${encodeURIComponent(insured.id)}/drivers`,
      { headers: { Authorization: `Bearer ${authToken}`, NCToken: amsToken } }
    );
    if (!res.ok) throw new Error('Drivers fetch failed');
    const data = await res.json();
    return Array.isArray(data) ? data : (data?.drivers ?? []);
  }, []);

  const fetchVehicles = useCallback(async (insured: InsuredResult): Promise<VehicleDetail[]> => {
    const authToken = localStorage.getItem('authToken') || '';
    const amsToken  = localStorage.getItem('amsToken')  || '';
    const res = await fetch(
      `${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/ampnowcerts/insured/${encodeURIComponent(insured.id)}/vehicles`,
      { headers: { Authorization: `Bearer ${authToken}`, NCToken: amsToken } }
    );
    if (!res.ok) throw new Error('Vehicles fetch failed');
    const data = await res.json();
    return Array.isArray(data) ? data : (data?.vehicles ?? []);
  }, []);

  // ── Open detail panel ─────────────────────────────────────────────────────────
  const openDetail = useCallback(async (insured: InsuredResult, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Toggle off if same insured
    if (detail?.insured.id === insured.id) {
      setDetail(null);
      return;
    }

    const rt = getRecordType(insured);

    if (rt === 'carrier') {
      // Carrier: only fetch policies written with this carrier
      setDetail({
        insured, tab: 'policies',
        policies: [], drivers: [], vehicles: [],
        loadingPolicies: true, loadingDrivers: false, loadingVehicles: false,
      });

      const result = await Promise.allSettled([fetchCarrierPolicies(insured)]);
      const [policies] = result;
      setDetail(prev => {
        if (!prev || prev.insured.id !== insured.id) return prev;
        return {
          ...prev,
          policies:        policies.status === 'fulfilled' ? policies.value : [],
          loadingPolicies: false,
        };
      });
    } else {
      // Insured/Prospect: fetch policies, drivers, vehicles in parallel
      setDetail({
        insured, tab: 'policies',
        policies: [], drivers: [], vehicles: [],
        loadingPolicies: true, loadingDrivers: true, loadingVehicles: true,
      });

      const [policies, drivers, vehicles] = await Promise.allSettled([
        fetchPolicies(insured),
        fetchDrivers(insured),
        fetchVehicles(insured),
      ]);

      setDetail(prev => {
        if (!prev || prev.insured.id !== insured.id) return prev;
        return {
          ...prev,
          policies:        policies.status  === 'fulfilled' ? policies.value  : [],
          drivers:         drivers.status   === 'fulfilled' ? drivers.value   : [],
          vehicles:        vehicles.status  === 'fulfilled' ? vehicles.value  : [],
          loadingPolicies: false,
          loadingDrivers:  false,
          loadingVehicles: false,
        };
      });
    }
  }, [detail, fetchPolicies, fetchDrivers, fetchVehicles, fetchCarrierPolicies]);

  const switchTab = useCallback((tab: DetailTab, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDetail(prev => prev ? { ...prev, tab } : null);
  }, []);

  const showHint = query.length < 2;

  return (
    <div className={`imd${detail ? ' imd--expanded' : ''}`} role="listbox" aria-label="Search results">

      {/* ── Main insured list ── */}
      <div className="imd__main">
        <div className="imd__header">
          <span className="imd__header-dot" />
          <span>Insured · Prospect · Policy</span>
          {!showHint && query && <span className="imd__header-query">"{query}"</span>}
        </div>

        <div className="imd__body">
          {loading ? (
            <div className="imd__state">
              <div className="imd__spinner" />
              <span>Searching…</span>
            </div>
          ) : showHint ? (
            <div className="imd__state imd__state--hint">
              <FiUser size={14} />
              <span>Type to search insureds, prospects or policies</span>
            </div>
          ) : results.length === 0 ? (
            <div className="imd__state">No results for "{query}"</div>
          ) : (
            results.map((ins, idx) => {
              const rt       = getRecordType(ins);
              const name     = rt === 'policy' ? (ins.policyNumber || ins.name || 'Unknown') : (ins.name || 'Unknown');
              const initials = getInitials(name);
              const isActive   = idx === activeIdx;
              const isExpanded = detail?.insured.id === ins.id;
              const location   = [ins.city, ins.stateAbv].filter(Boolean).join(', ');
              const meta       = rt === 'policy'
                ? (ins.name || '')
                : [location, ins.email].filter(Boolean).join(' · ');

              return (
                <div key={`${ins.id}-${idx}`} className={`imd__row${isExpanded ? ' imd__row--expanded' : ''}`}>
                  <button
                    role="option"
                    aria-selected={isActive}
                    className={`imd__item${isActive ? ' imd__item--active' : ''}`}
                    onMouseDown={e => { e.preventDefault(); onSelect(ins); }}
                    onMouseEnter={() => onActiveChange(idx)}
                    type="button"
                  >
                    <div className={`imd__avatar imd__avatar--${rt}`}>
                      {initials || getTypeIcon(rt)}
                    </div>
                    <div className="imd__info">
                      <span className="imd__name">{name}</span>
                      {meta && <span className="imd__meta">{meta}</span>}
                    </div>
                    <span className={`imd__badge imd__badge--${rt}`}>
                      {ins.typeName || 'Insured'}
                    </span>
                  </button>

                  {/* Expand button — for Insured and Carrier */}
                  {(rt === 'insured' || rt === 'carrier') && (
                    <button
                      className={`imd__expand-btn${isExpanded ? ' imd__expand-btn--active' : ''}`}
                      onMouseDown={e => openDetail(ins, e)}
                      title="View details"
                      type="button"
                    >
                      <IcoExternalLink size={14} />
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── Detail panel (Policies · Drivers · Vehicles) ── */}
      {detail && (
        <div className="imd__detail">
          {/* Panel header */}
          <div className="imd__detail-header">
            <div className="imd__detail-avatar">
              {getInitials(detail.insured.name) || <FiUser size={12} />}
            </div>
            <span className="imd__detail-name">{detail.insured.name}</span>
            <button
              className="imd__detail-close"
              onMouseDown={e => { e.preventDefault(); setDetail(null); }}
              type="button"
            >
              <FiX size={11} />
            </button>
          </div>

          {/* Tab bar — Policies only for Carrier, Policies/Drivers/Vehicles for others */}
          <div className="imd__tabs">
            {getRecordType(detail.insured) === 'carrier' ? (
              <button
                className="imd__tab imd__tab--active"
                onMouseDown={e => switchTab('policies', e)}
                type="button"
              >
                Policies
                {detail.loadingPolicies
                  ? <span className="imd__tab-spin" />
                  : detail.policies.length > 0 && <span className="imd__tab-count">{detail.policies.length}</span>}
              </button>
            ) : (
              (['policies', 'drivers', 'vehicles'] as DetailTab[]).map(tab => {
                const count = tab === 'policies' ? detail.policies.length
                  : tab === 'drivers'  ? detail.drivers.length
                  : detail.vehicles.length;
                const isLoading = tab === 'policies' ? detail.loadingPolicies
                  : tab === 'drivers'  ? detail.loadingDrivers
                  : detail.loadingVehicles;
                const label = tab === 'policies' ? 'Policies'
                  : tab === 'drivers' ? 'Drivers' : 'Vehicles';
                return (
                  <button
                    key={tab}
                    className={`imd__tab${detail.tab === tab ? ' imd__tab--active' : ''}`}
                    onMouseDown={e => switchTab(tab, e)}
                    type="button"
                  >
                    {label}
                    {isLoading
                      ? <span className="imd__tab-spin" />
                      : count > 0 && <span className="imd__tab-count">{count}</span>}
                  </button>
                );
              })
            )}
          </div>

          {/* Tab body */}
          <div className="imd__detail-body">
            {detail.tab === 'policies' && (
              detail.loadingPolicies ? <LoadingState /> :
              detail.policies.length === 0 ? <EmptyState label="No policies found" /> :
              detail.policies.map((pol, i) => (
                <button key={`pol-${pol.id}-${i}`} className="imd__detail-item"
                  onMouseDown={e => { e.preventDefault(); onSelect(pol); }} type="button">
                  <div className="imd__detail-item-icon imd__detail-item-icon--policy">
                    <FiFileText size={10} />
                  </div>
                  <div className="imd__detail-item-info">
                    <span className="imd__detail-item-title">{pol.policyNumber || pol.name || '—'}</span>
                    {pol.name && pol.policyNumber && (
                      <span className="imd__detail-item-sub">{pol.name}</span>
                    )}
                  </div>
                </button>
              ))
            )}

            {detail.tab === 'drivers' && (
              detail.loadingDrivers ? <LoadingState /> :
              detail.drivers.length === 0 ? <EmptyState label="No drivers found" /> :
              detail.drivers.map((d, i) => {
                const name = [d.firstName, d.lastName].filter(Boolean).join(' ') || '—';
                const sub  = [d.gender, d.maritalStatus, d.licenseNumber ? `#${d.licenseNumber}` : null].filter(Boolean).join(' · ');
                return (
                  <div key={`drv-${d.databaseId}-${i}`} className="imd__detail-item imd__detail-item--static">
                    <div className="imd__detail-item-icon imd__detail-item-icon--driver">
                      <FiUser size={10} />
                    </div>
                    <div className="imd__detail-item-info">
                      <span className="imd__detail-item-title">
                        {name}
                        {d.excludedDriver && <span className="imd__detail-badge imd__detail-badge--warn">Excl.</span>}
                      </span>
                      {sub && <span className="imd__detail-item-sub">{sub}</span>}
                      {d.dateOfBirth && <span className="imd__detail-item-sub">DOB: {fmtDate(d.dateOfBirth)}</span>}
                    </div>
                  </div>
                );
              })
            )}

            {detail.tab === 'vehicles' && (
              detail.loadingVehicles ? <LoadingState /> :
              detail.vehicles.length === 0 ? <EmptyState label="No vehicles found" /> :
              detail.vehicles.map((v, i) => {
                const title = [v.year, v.make, v.model].filter(Boolean).join(' ') || '—';
                const sub   = [v.bodyType, v.use, v.vin ? `VIN: ${v.vin}` : null].filter(Boolean).join(' · ');
                return (
                  <div key={`veh-${v.databaseId}-${i}`} className="imd__detail-item imd__detail-item--static">
                    <div className="imd__detail-item-icon imd__detail-item-icon--vehicle">
                      <CarIcon size={10} />
                    </div>
                    <div className="imd__detail-item-info">
                      <span className="imd__detail-item-title">{title}</span>
                      {sub && <span className="imd__detail-item-sub">{sub}</span>}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Small reusable sub-components ──────────────────────────────────────────────
const LoadingState: React.FC = () => (
  <div className="imd__skel">
    {[70, 50, 85, 60, 75].map((w, i) => (
      <div key={i} className="imd__skel-row">
        <div className="imd__skel-icon" />
        <div className="imd__skel-lines">
          <div className="imd__skel-line imd__skel-line--title" style={{ width: `${w}%` }} />
          <div className="imd__skel-line imd__skel-line--sub"   style={{ width: `${w - 18}%` }} />
        </div>
      </div>
    ))}
  </div>
);

const EmptyState: React.FC<{ label: string }> = ({ label }) => (
  <div className="imd__state imd__state--empty">
    <FiList size={13} style={{ opacity: 0.35 }} />
    <span>{label}</span>
  </div>
);

const CarIcon: React.FC<{ size?: number }> = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v4"/>
    <circle cx="17" cy="17" r="3"/><circle cx="9" cy="17" r="3"/>
    <path d="M13 17H12M6 11h11"/>
  </svg>
);

export default InsuredMentionDropdown;

